import React, { Component, FormEvent } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Select, { SelectOption } from "../Select";

const TRANSLATION_KEY = "Forms.DateSelector";

export interface DateSelectorState {
    values: {
        day: number;
        month: number;
        year: number;
    };
    errored: boolean;
}

export interface DateSelectorProps {
    onChange: (event: any) => void;
    value?: Date;
    rangeStart: Date;
    rangeEnd: Date;
    type: "MDY" | "MY";
    [index: string]: any;
}

class DateSelector extends Component<DateSelectorProps & WithTranslation, DateSelectorState> {
    constructor(props: DateSelectorProps & WithTranslation) {
        super(props);

        this.state = {
            errored: false,
            values: this.getValuesFromDate(this.props.value),
        };
    }

    public render() {
        return (
            <div>
                <Select {...this.getSelectProps("month")} />
                {this.shouldDisplayDay() && <Select {...this.getSelectProps("day")} />}
                <Select {...this.getSelectProps("year")} />
            </div>
        );
    }

    private getSelectProps = (selectName: "day" | "month" | "year") => {
        const { error, name, onBlur, onChange, rangeEnd, rangeStart, t, tReady, value, ...rest } = this.props;
        const placeholders = {
            day: t(`${TRANSLATION_KEY}.dayPlaceholder`),
            month: t(`${TRANSLATION_KEY}.monthPlaceholder`),
            year: t(`${TRANSLATION_KEY}.yearPlaceholder`),
        };
        const { values } = this.state;

        return {
            error: selectName === "day" ? error || this.state.errored : error,
            name: `${name}-${selectName}`,
            onChange: (event: any) => this.handleInputChange(event, selectName),
            options: this.getOptions(selectName),
            placeholder: placeholders[selectName],
            style: this.getSelectStyle(selectName),
            value: values[selectName],
            ...rest,
        };
    };

    private getSelectStyle = (selectName: "day" | "month" | "year") => {
        return {
            display: "inline-block",
            marginLeft: selectName === "year" || (selectName === "day" && this.shouldDisplayDay()) ? "10px" : 0,
            marginRight: selectName !== "year" ? "10px" : 0,
            width: this.shouldDisplayDay()
                ? // IE11 hack - without decimal it rounds pixels up and breaks layout
                  "calc(100%/3 - 40.1px/3)"
                : "calc(100%/2 - 20.1px/2)",
        };
    };

    private getValuesFromDate = (date?: Date) => {
        return {
            day: date ? date.getUTCDate() : -1,
            month: date ? date.getUTCMonth() : -1,
            year: date ? date.getUTCFullYear() : -1,
        };
    };

    private handleInputChange = (event: FormEvent<HTMLInputElement>, selectName: "day" | "month" | "year") => {
        const target = event.target as HTMLInputElement;
        const value = parseInt(target.value, 10);

        this.setState(
            {
                values: { ...this.state.values, [selectName]: value },
            },
            this.handleChange
        );
    };

    private handleChange = () => {
        const { day, month, year } = this.state.values;

        if (this.isDateSelected(day, month, year)) {
            if (this.shouldDisplayDay() ? this.isValidDate(day, month, year) : this.isValidDate(1, month, year)) {
                this.setState({ errored: false }, () => {
                    this.props.onChange({
                        target: {
                            name: this.props.name,
                            value: new Date(year, month, this.shouldDisplayDay() ? day : 1),
                        },
                    });
                    this.handleBlur(new Date(year, month, this.shouldDisplayDay() ? day : 1));
                });
            } else {
                this.setState(
                    {
                        errored: true,
                        values: { ...this.state.values, day: -1 },
                    },
                    () => {
                        this.props.onChange({
                            target: {
                                name: this.props.name,
                                value: "",
                            },
                        });
                        this.handleBlur("");
                    }
                );
            }
        }
    };

    private handleBlur = (value: any) => {
        this.props.onBlur({
            target: {
                name: this.props.name,
                value,
            },
        });
    };

    private getOptions = (selectName: "day" | "month" | "year"): SelectOption[] => {
        const options = {
            day: this.getDayOptions(),
            month: this.getMonthOptions(),
            year: this.getYearOptions(),
        };

        return options[selectName];
    };

    private getDayOptions = (): SelectOption[] => {
        const days = [];
        for (let index = 1; index < 32; index++) {
            days.push({
                label: this.addLeadingZero(index),
                value: index,
            });
        }
        return days;
    };

    private getMonthOptions = (): SelectOption[] => {
        const { t } = this.props;

        const monthNames = [
            t(`${TRANSLATION_KEY}.january`),
            t(`${TRANSLATION_KEY}.february`),
            t(`${TRANSLATION_KEY}.march`),
            t(`${TRANSLATION_KEY}.april`),
            t(`${TRANSLATION_KEY}.may`),
            t(`${TRANSLATION_KEY}.june`),
            t(`${TRANSLATION_KEY}.july`),
            t(`${TRANSLATION_KEY}.august`),
            t(`${TRANSLATION_KEY}.september`),
            t(`${TRANSLATION_KEY}.october`),
            t(`${TRANSLATION_KEY}.november`),
            t(`${TRANSLATION_KEY}.december`),
        ];
        return monthNames.map((name, index) => ({
            label: name,
            value: index,
        }));
    };

    private getYearOptions = (): SelectOption[] => {
        const { rangeStart, rangeEnd } = this.props;
        const years = [];

        for (let year = rangeStart.getFullYear(); year <= rangeEnd.getFullYear(); year++) {
            years.unshift({ label: year.toString(), value: year });
        }

        return years;
    };

    private shouldDisplayDay = (): boolean => {
        return this.props.type === "MDY";
    };

    private addLeadingZero = (num: number): string => {
        const s = "0" + num;
        return s.substr(s.length - 2);
    };

    private isDateSelected = (day: any, month: any, year: any): boolean => {
        return this.shouldDisplayDay() ? day !== -1 && month !== -1 && year !== -1 : month !== -1 && year !== -1;
    };

    private isValidDate = (day: number, month: number, year: number): boolean => {
        return month >= 0 && month < 12 && day > 0 && day <= this.daysInMonth(month, year);
    };

    private daysInMonth = (month: number, year: number): number => {
        switch (month) {
            case 1:
                return (year % 4 === 0 && year % 100) || year % 400 === 0 ? 29 : 28;
            case 8:
            case 3:
            case 5:
            case 10:
                return 30;
            default:
                return 31;
        }
    };
}

export default withTranslation()(DateSelector);
